/**
 * A ceremony batch's columns (spec 2026-10-02 upacara bersama, 3.2–3.4). The ceremony's own
 * fields (the number, date, place and minister, with the book and page) are typed once; a row
 * holds the type's other fields of the single form, in book order, and requires exactly what
 * the single form requires of them (`KOLOM_WAJIB_BUKU`; no "Tidak tercatat" tick: a batch is a
 * new record). After them come the extras for matching and for a new person: NIK, the Catholic
 * KK number (Baptis records it already), Hubungan, Lingkungan and the person's own address,
 * then "Bukan umat paroki ini" (ya/tidak, owner 2026-10-04: ya records the entry in the book
 * alone, linked to no one, and the extras for a new person go unused). These columns are the
 * file's and the template's; a header is matched by the column's own label and by the words a
 * catechist's list uses.
 */
import { type FormatKolom, type KolomTarget, kolomDalamSebutan } from '../import/mapping'
import { kunciTeks } from '../import/normalise'
import { ISIAN_BUKU, type RegisterField } from '../registers'
import ui from '../strings/ui.id.json' with { type: 'json' }
import { isiUnit, SEBUTAN_BAWAAN, type SebutanUnit } from '../strings/unit'
import { KOLOM_WAJIB_BUKU } from '../validators/register'

export const TABEL_UPACARA = ['baptis', 'komuni_pertama', 'krisma'] as const
export type TabelUpacara = (typeof TABEL_UPACARA)[number]

export function isTabelUpacara(v: unknown): v is TabelUpacara {
  return typeof v === 'string' && (TABEL_UPACARA as readonly string[]).includes(v)
}

/** The roles that write the registers (spec 3.1); a ketua reads no book. */
export function bolehUpacara(peran: string | null | undefined): boolean {
  return peran === 'admin_paroki' || peran === 'staf_paroki'
}

/** The ceremony's own fields, typed once on the first step with the book and the page. */
export const KOLOM_UPACARA = ['nomor', 'tanggal', 'tempat', 'pelayan'] as const

const MILIK_UPACARA: ReadonlySet<string> = new Set(KOLOM_UPACARA)

/** A row's fields: the type's fields without the ceremony's, in book order. */
export function isianPeserta(tabel: TabelUpacara): readonly RegisterField[] {
  return ISIAN_BUKU[tabel].filter((f) => !MILIK_UPACARA.has(f.name))
}

/** What a row must fill: the book's required columns less the ceremony's. */
export function wajibPeserta(tabel: TabelUpacara): readonly string[] {
  return KOLOM_WAJIB_BUKU[tabel].filter((k) => !MILIK_UPACARA.has(k))
}

/**
 * For matching and for a new person (spec 3.4, Part B), after a row's own fields; last, whether
 * the person is no umat of this parish and only recorded in the book (owner, 2026-10-04).
 */
export const KOLOM_TAMBAHAN = [
  'nik',
  'nomor_kk_katolik',
  'hubungan',
  'lingkungan',
  'alamat',
  'rt_rw',
  'kampung_desa',
  'kecamatan',
  'kabupaten_kota',
  'provinsi',
  'bukan_umat',
] as const
export type KolomTambahan = (typeof KOLOM_TAMBAHAN)[number]

/** The extras a type's rows carry: those not already one of its fields. */
export function kolomTambahan(tabel: TabelUpacara): readonly KolomTambahan[] {
  const ada = new Set(isianPeserta(tabel).map((f) => f.name))
  return KOLOM_TAMBAHAN.filter((k) => !ada.has(k))
}

const K = ui.impor.kolom
const LABEL_TAMBAHAN: Readonly<Record<KolomTambahan, string>> = {
  nik: K.nik,
  nomor_kk_katolik: K.nomor_kk_katolik,
  hubungan: K.hubungan,
  lingkungan: K.lingkungan,
  alamat: K.alamat,
  rt_rw: K.rt_rw,
  kampung_desa: K.kampung_desa,
  kecamatan: K.kecamatan,
  kabupaten_kota: K.kabupaten_kota,
  provinsi: K.provinsi,
  bukan_umat: ui.upacara.bukanUmat.kolom,
}

/** Header words of a catechist's list, as alias keys (`kunciTeks`), besides each label's own. */
const ALIAS: Readonly<Record<string, readonly string[]>> = {
  nama: ['nama', 'nama peserta', 'nama calon', 'nama anak', 'nama umat', 'name'],
  nama_baptis: ['nama baptis', 'nama permandian', 'baptis'],
  nama_krisma: ['nama krisma', 'nama penguatan'],
  tempat_lahir: ['tempat lahir', 'tmp lahir', 'tempat lhr'],
  tanggal_lahir: ['tanggal lahir', 'tgl lahir', 'tgl lhr', 'lahir'],
  jenis_kelamin: ['jenis kelamin', 'jk', 'l p', 'kelamin', 'gender'],
  nama_ayah: ['nama ayah', 'ayah', 'bapak'],
  nama_ibu: ['nama ibu', 'ibu'],
  agama_ayah: ['agama ayah'],
  agama_ibu: ['agama ibu'],
  wali_1: ['wali baptis 1', 'wali baptis', 'wali 1', 'wali', 'bapa ibu permandian'],
  wali_2: ['wali baptis 2', 'wali 2'],
  wali_krisma: ['wali krisma', 'wali'],
  jenis: ['jenis baptis', 'jenis'],
  referensi_baptis: ['rujukan baptis', 'referensi baptis', 'surat baptis'],
  // Release 18: the family's government KK number, its Kode keluarga, or its parish number.
  nomor_kk_katolik: [
    'nomor kk',
    'no kk',
    'kk',
    'nomor kartu keluarga',
    'no kartu keluarga',
    'kartu keluarga',
    'kode keluarga',
    'nomor kk katolik',
    'no kk katolik',
    'kk katolik',
  ],
  catatan: ['catatan', 'keterangan', 'ket'],
  nik: ['nik', 'no nik', 'nomor induk kependudukan'],
  hubungan: ['hubungan', 'hubungan keluarga', 'shdk'],
  lingkungan: ['lingkungan', 'nama lingkungan', 'kombas', 'kbg'],
  alamat: ['alamat', 'alamat rumah', 'jalan'],
  rt_rw: ['rt rw', 'rt', 'rw'],
  kampung_desa: ['kampung', 'desa', 'kelurahan', 'kampung desa'],
  kecamatan: ['distrik', 'kecamatan'],
  kabupaten_kota: ['kabupaten kota', 'kabupaten', 'kota'],
  provinsi: ['provinsi'],
  bukan_umat: [
    'bukan umat',
    'bukan umat paroki',
    'hanya di buku',
    'hanya catat di buku',
    'hanya dicatat di buku',
    'umat luar',
    'luar paroki',
  ],
}

const FORMAT: Readonly<Record<string, FormatKolom>> = {
  tanggal_lahir: 'tanggal',
  jenis_kelamin: 'lp',
  jenis: 'jenisBaptis',
  nik: 'angka16',
  nomor_kk_katolik: 'nomorKk',
  hubungan: 'hubungan',
  lingkungan: 'lingkungan',
  bukan_umat: 'yaTidak',
}

const CONTOH: Readonly<Record<string, string>> = ui.upacara.contoh

function target(id: string, label: string, wajib: boolean): KolomTarget {
  return {
    id,
    label,
    wajib,
    format: FORMAT[id] ?? 'teks',
    // A label in the unit's word ("{unit}") is its default word here; the parish's own joins
    // the aliases with `kolomDalamSebutan`.
    alias: [kunciTeks(isiUnit(label, SEBUTAN_BAWAAN)), ...(ALIAS[id] ?? [])],
    contoh: CONTOH[id] ?? '',
  }
}

/** The file's and the template's columns of a type: the row's fields, then the extras. */
const TARGET_UPACARA = new Map<TabelUpacara, readonly KolomTarget[]>()

/** A type's columns, the lingkungan column in the parish's word (Sebutan unit terkecil). */
export function kolomTargetUpacara(
  tabel: TabelUpacara,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): readonly KolomTarget[] {
  let dasar = TARGET_UPACARA.get(tabel)
  if (dasar === undefined) {
    const wajib = new Set(wajibPeserta(tabel))
    dasar = [
      ...isianPeserta(tabel).map((f) => target(f.name, f.label, wajib.has(f.name))),
      ...kolomTambahan(tabel).map((k) => target(k, LABEL_TAMBAHAN[k], false)),
    ]
    TARGET_UPACARA.set(tabel, dasar)
  }
  return kolomDalamSebutan(dasar, sebutan)
}

/**
 * The same columns for the mapping step: only the name must come from the file; the book's
 * other required columns may be completed in the grid (the row is "Bermasalah" until then).
 */
export function kolomPetaUpacara(
  tabel: TabelUpacara,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): readonly KolomTarget[] {
  return kolomTargetUpacara(tabel, sebutan).map((k) => ({ ...k, wajib: k.id === 'nama' }))
}
